import React from "react";
import { ChartLineUp, Icon as PhosphorIcon, Megaphone, ShieldCheck, Sparkle } from "@phosphor-icons/react";
import ConsentToggle from "components/common/consent/ConsentToggle";
import { useLocale } from "lib/hooks/useLocale";
import { Consent, ConsentAnswers } from "lib/types/consent";

// Icons from the design by consent code; anything else gets a generic one.
const CONSENT_ICONS: Record<string, PhosphorIcon> = {
  ANALYTICS: ChartLineUp,
  ADVERTISING: Megaphone,
  FUNCTIONAL: Sparkle,
};

interface ConsentPreferenceListProps {
  consents: Consent[];
  answers: ConsentAnswers;
  onChange: (code: string, isAccepted: boolean) => void;
  onReadMore: (consent: Consent) => void;
}

const ConsentPreferenceList: React.FC<ConsentPreferenceListProps> = ({
  consents,
  answers,
  onChange,
  onReadMore,
}) => {
  const { t } = useLocale();

  return (
    <ul className="flex w-full flex-col gap-4">
      {consents.map((consent) => {
        const ConsentIcon = CONSENT_ICONS[consent.code] ?? ShieldCheck;
        const isAccepted = consent.is_mandatory || Boolean(answers[consent.code]);

        return (
          <li key={consent.code} className="flex w-full items-start gap-3 px-6">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <ConsentIcon size={32} weight="duotone" className="shrink-0 text-[#4d4d4d]" />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="text-sm font-medium leading-[22px] tracking-[-0.14px] text-[#4d4d4d]">
                  {consent.title}
                </p>
                <p className="text-xs leading-[18px] tracking-[-0.12px] text-[#4d4d4d]/70">
                  {consent.description ? `${consent.description} ` : ""}
                  <button
                    type="button"
                    onClick={() => onReadMore(consent)}
                    className="font-medium text-[#27272a]">
                    {t("consent_read_more")}
                  </button>
                </p>
              </div>
            </div>
            <ConsentToggle
              checked={isAccepted}
              disabled={consent.is_mandatory}
              label={consent.title}
              onChange={(checked) => onChange(consent.code, checked)}
            />
          </li>
        );
      })}
    </ul>
  );
};

export default ConsentPreferenceList;
